import { z } from 'zod';
import type { NativeAppName, Stage } from '../enums';
import { assertTypes, type NoNewFields } from '../primitives';

export const zStorePlatform = z.literal(['ios', 'android']);
export type StorePlatform = z.output<typeof zStorePlatform>;

/**
 * The pos-native and admin-native store listings per stage: the bundle IDs that stage's builds
 * report, set in each app's `app.config.ts`. consumer-native's are the bundle IDs on each brand's
 * mobile app config.
 * - production: the App Store / Play Store bundle IDs. Internal production builds
 *   (`<bundle ID>.pro`) have no listing, so no store update.
 * - dev and staging: their internal builds (`<bundle ID>.<stage>`), so a store update can be tried
 *   on that stage. They aren't in a store, so they have no store link.
 */
export const PLATFORM_APP_STORE_LISTINGS = {
  production: [
    { app: 'pos-native', bundleId: 'au.com.orderhub.pos', platform: 'ios' },
    { app: 'admin-native', bundleId: 'com.orderhub.backoffice', platform: 'ios' },
    { app: 'admin-native', bundleId: 'com.orderhub.backoffice', platform: 'android' },
  ],
  staging: [
    { app: 'pos-native', bundleId: 'com.tripleshot.pos.sta', platform: 'ios' },
    { app: 'admin-native', bundleId: 'com.tripleshot.admin.sta', platform: 'ios' },
    { app: 'admin-native', bundleId: 'com.tripleshot.admin.sta', platform: 'android' },
  ],
  dev: [
    { app: 'pos-native', bundleId: 'com.tripleshot.pos.dev', platform: 'ios' },
    { app: 'admin-native', bundleId: 'com.tripleshot.admin.dev', platform: 'ios' },
    { app: 'admin-native', bundleId: 'com.tripleshot.admin.dev', platform: 'android' },
  ],
} as const satisfies Record<
  Stage,
  readonly { app: NativeAppName; bundleId: string; platform: StorePlatform }[]
>;

export const appleAppStoreInfoSchema = z.object({
  appStoreVersion: z.string().nullable().default(null),
  appStoreUrl: z.string().nullable().default(null),
  releaseNotes: z.string().nullable().default(null),
});
export type AppleAppStoreInfo = z.output<typeof appleAppStoreInfoSchema>;

/**
 * When one store listing (bundle ID + platform) offers or forces a store update. A device compares
 * its native version (the major part of its store version, `N.0.0`) with these.
 */
export const appUpdateNativePolicySchema = z.object({
  /** Bundle identifier of the listing, e.g. "au.com.orderhub.pos". */
  bundleId: z.string(),
  platform: zStorePlatform,
  /** Below it, the store update is offered. `null`: none offered. */
  latestNativeVersion: z.number().nullable().default(null),
  /** Below it, the store update is forced. `null`: none forced. */
  minNativeVersion: z.number().nullable().default(null),
  /** The App Store listing, looked up from Apple when the iOS latest version changes. */
  appleAppStoreInfo: appleAppStoreInfoSchema.nullable().default(null),

  _creationTime: z.number().optional(),
  _id: z.string().optional(),
});
export type AppUpdateNativePolicy = z.output<typeof appUpdateNativePolicySchema>;

export const appUpdateNativePolicyStrictSchema = appUpdateNativePolicySchema
  .safeExtend({
    bundleId: z.string().min(1),
    latestNativeVersion: z.int().min(1).nullable(),
    minNativeVersion: z.int().min(1).nullable(),
  })
  .refine(
    ({ latestNativeVersion, minNativeVersion }) =>
      minNativeVersion === null ||
      (latestNativeVersion !== null && minNativeVersion <= latestNativeVersion),
    { message: "Can't be above the latest version", path: ['minNativeVersion'] },
  );

export const appUpdateNativePolicyCvxSchema = appUpdateNativePolicySchema.safeExtend({
  _creationTime: z.number(),
  _id: z.string(),
});
export type AppUpdateNativePolicyCvx = z.output<typeof appUpdateNativePolicyCvxSchema>;

assertTypes<NoNewFields<AppUpdateNativePolicy, AppUpdateNativePolicyCvx>>();
